/**
 * Live Wall merged windows — the geometry, shared by both products' walls.
 *
 * A wall is a fixed grid (`'3x3'` = 3 rows × 3 columns, the same RxC reading the grid-size menu
 * and the backend's LIVE_WALL_GRIDS use) plus a list of MERGES: rectangles of cells that play
 * as ONE window. A merge is stored by its top-left cell (`position`, the same row-major index
 * a camera slot uses) and its span, so a camera placed in a merged window is simply a camera at
 * the merge's `position` — the slot format did not change, and a layout with no merges is
 * byte-for-byte what it was before.
 *
 * Merges are always RECTANGLES. That is not a simplification of the UI, it is what a CSS grid
 * (and a video) can draw: an L-shaped window has no aspect ratio to play into.
 *
 * Keep `normalizeMerges` in step with the node backend's liveWallService, which applies the
 * same three rules on save and on read (inside the grid, bigger than one cell, no overlap).
 */

export interface WallMerge {
  /** Top-left cell, row-major: row * cols + col. */
  position: number;
  colSpan: number;
  rowSpan: number;
}

/** One thing drawn on the wall: a single cell, or a merged block of them. */
export interface WallWindow {
  position: number;
  row: number;
  col: number;
  rowSpan: number;
  colSpan: number;
  merged: boolean;
}

/** Inclusive cell rectangle. */
export interface CellRect {
  r0: number;
  c0: number;
  r1: number;
  c1: number;
}

/** `'3x4'` → 3 rows, 4 columns. Unknown strings fall back to 2×2 rather than a zero grid. */
export function gridDims(gridSize: string): { rows: number; cols: number } {
  const [r, c] = String(gridSize || '').split('x').map((n) => parseInt(n, 10));
  if (!Number.isFinite(r) || !Number.isFinite(c) || r < 1 || c < 1) return { rows: 2, cols: 2 };
  return { rows: r, cols: c };
}

export const rectCells = (r: CellRect) => (r.r1 - r.r0 + 1) * (r.c1 - r.c0 + 1);

const mergeRect = (m: WallMerge, cols: number): CellRect => {
  const r0 = Math.floor(m.position / cols);
  const c0 = m.position % cols;
  return { r0, c0, r1: r0 + m.rowSpan - 1, c1: c0 + m.colSpan - 1 };
};

const overlaps = (a: CellRect, b: CellRect) =>
  a.r0 <= b.r1 && b.r0 <= a.r1 && a.c0 <= b.c1 && b.c0 <= a.c1;

const contains = (outer: CellRect, inner: CellRect) =>
  outer.r0 <= inner.r0 && outer.c0 <= inner.c0 && outer.r1 >= inner.r1 && outer.c1 >= inner.c1;

/**
 * The merges this grid can actually draw: inside the grid, more than one cell, and not
 * overlapping an earlier one (first wins). Anything else is dropped, never "repaired" —
 * guessing what a broken rectangle meant would move someone's cameras.
 */
export function normalizeMerges(merges: WallMerge[] | null | undefined, gridSize: string): WallMerge[] {
  const { rows, cols } = gridDims(gridSize);
  const kept: WallMerge[] = [];
  const rects: CellRect[] = [];
  for (const m of Array.isArray(merges) ? merges : []) {
    if (!m || !Number.isInteger(m.position) || !Number.isInteger(m.colSpan) || !Number.isInteger(m.rowSpan)) continue;
    if (m.position < 0 || m.colSpan < 1 || m.rowSpan < 1 || m.colSpan * m.rowSpan < 2) continue;
    const r = mergeRect(m, cols);
    if (r.r1 >= rows || r.c1 >= cols) continue;
    if (rects.some((o) => overlaps(o, r))) continue;
    rects.push(r);
    kept.push({ position: m.position, colSpan: m.colSpan, rowSpan: m.rowSpan });
  }
  return kept.sort((a, b) => a.position - b.position);
}

/** Every window on the wall, in reading order. Cells a merge covers are not windows. */
export function buildWindows(gridSize: string, merges: WallMerge[]): WallWindow[] {
  const { rows, cols } = gridDims(gridSize);
  const byAnchor = new Map<number, WallMerge>();
  const covered = new Set<number>();
  for (const m of normalizeMerges(merges, gridSize)) {
    byAnchor.set(m.position, m);
    const r = mergeRect(m, cols);
    for (let rr = r.r0; rr <= r.r1; rr++) {
      for (let cc = r.c0; cc <= r.c1; cc++) {
        const p = rr * cols + cc;
        if (p !== m.position) covered.add(p);
      }
    }
  }
  const out: WallWindow[] = [];
  for (let p = 0; p < rows * cols; p++) {
    if (covered.has(p)) continue;
    const m = byAnchor.get(p);
    out.push({
      position: p,
      row: Math.floor(p / cols),
      col: p % cols,
      rowSpan: m?.rowSpan ?? 1,
      colSpan: m?.colSpan ?? 1,
      merged: !!m,
    });
  }
  return out;
}

/** The CSS grid placement for a window or a selection rectangle. */
export function gridArea(x: { row: number; col: number; rowSpan: number; colSpan: number }) {
  return {
    gridRow: `${x.row + 1} / span ${x.rowSpan}`,
    gridColumn: `${x.col + 1} / span ${x.colSpan}`,
  };
}

export const rectArea = (r: CellRect) =>
  gridArea({ row: r.r0, col: r.c0, rowSpan: r.r1 - r.r0 + 1, colSpan: r.c1 - r.c0 + 1 });

/**
 * The rectangle between two windows, grown until it cuts no existing merge in half.
 *
 * Dragging from a single cell into the middle of a 2×2 block cannot mean "half of that
 * block", so the selection swallows the whole block — and may then touch another, hence the
 * loop. It always terminates: the rectangle only grows, and the grid bounds it.
 */
export function selectionBetween(a: WallWindow, b: WallWindow, gridSize: string, merges: WallMerge[]): CellRect {
  const { cols } = gridDims(gridSize);
  const r: CellRect = {
    r0: Math.min(a.row, b.row),
    c0: Math.min(a.col, b.col),
    r1: Math.max(a.row + a.rowSpan - 1, b.row + b.rowSpan - 1),
    c1: Math.max(a.col + a.colSpan - 1, b.col + b.colSpan - 1),
  };
  const rects = normalizeMerges(merges, gridSize).map((m) => mergeRect(m, cols));
  let grew = true;
  while (grew) {
    grew = false;
    for (const m of rects) {
      if (overlaps(r, m) && !contains(r, m)) {
        r.r0 = Math.min(r.r0, m.r0); r.c0 = Math.min(r.c0, m.c0);
        r.r1 = Math.max(r.r1, m.r1); r.c1 = Math.max(r.c1, m.c1);
        grew = true;
      }
    }
  }
  return r;
}

/** Is this window inside the selection? (Selections never cut a window, so inside = touching.) */
export const windowInRect = (w: WallWindow, r: CellRect) =>
  w.row >= r.r0 && w.col >= r.c0 && w.row + w.rowSpan - 1 <= r.r1 && w.col + w.colSpan - 1 <= r.c1;

/** The selection is exactly one existing window — merging it would change nothing. */
export function rectIsOneWindow(r: CellRect, windows: WallWindow[]): boolean {
  return windows.filter((w) => windowInRect(w, r)).length <= 1;
}

/** Replace every merge inside `r` with one merge covering all of `r`. */
export function mergeInto(merges: WallMerge[], r: CellRect, gridSize: string): WallMerge[] {
  const { cols } = gridDims(gridSize);
  const rest = normalizeMerges(merges, gridSize).filter((m) => !contains(r, mergeRect(m, cols)));
  return normalizeMerges(
    [...rest, { position: r.r0 * cols + r.c0, colSpan: r.c1 - r.c0 + 1, rowSpan: r.r1 - r.r0 + 1 }],
    gridSize,
  );
}

export const splitAt = (merges: WallMerge[], position: number) =>
  merges.filter((m) => m.position !== position);

interface Slot { cameraId: string; cameraName: string; gridPosition: number }

/**
 * What merging `r` does to the cameras inside it: the FIRST one in reading order moves to the
 * new window's top-left cell, the rest come off the wall. Returned rather than applied so the
 * selection bar can say it ("keeps Gate 1 · removes 2") before the operator commits.
 */
export function camerasForMerge<T extends Slot>(
  grid: (T | null)[],
  r: CellRect,
  gridSize: string,
): { next: (T | null)[]; kept: T | null; removed: T[] } {
  const { cols } = gridDims(gridSize);
  const inside: T[] = [];
  for (let rr = r.r0; rr <= r.r1; rr++) {
    for (let cc = r.c0; cc <= r.c1; cc++) {
      const cam = grid[rr * cols + cc];
      if (cam) inside.push(cam);
    }
  }
  const anchor = r.r0 * cols + r.c0;
  const next = [...grid];
  for (let rr = r.r0; rr <= r.r1; rr++) {
    for (let cc = r.c0; cc <= r.c1; cc++) next[rr * cols + cc] = null;
  }
  const kept = inside[0] || null;
  if (kept) next[anchor] = { ...kept, gridPosition: anchor };
  return { next, kept, removed: inside.slice(1) };
}

/**
 * Ready-made shapes — the handful every control room asks for, one click instead of four
 * drags. Each is a grid size plus its merges; the cameras already on the wall flow into the
 * new windows largest-first, so the FIRST camera lands in the big window.
 */
export interface WallPreset {
  id: string;
  label: string;
  hint: string;
  gridSize: string;
  merges: WallMerge[];
}

export const WALL_PRESETS: WallPreset[] = [
  { id: '1+5', label: '1 + 5', hint: 'One large window, five small', gridSize: '3x3', merges: [{ position: 0, colSpan: 2, rowSpan: 2 }] },
  { id: '1+7', label: '1 + 7', hint: 'One very large window, seven around it', gridSize: '4x4', merges: [{ position: 0, colSpan: 3, rowSpan: 3 }] },
  { id: '1+8', label: '1 + 8', hint: 'Large window in the middle of a wide wall', gridSize: '3x4', merges: [{ position: 1, colSpan: 2, rowSpan: 2 }] },
  { id: '1+12', label: '1 + 12', hint: 'Large window in the centre, twelve around it', gridSize: '4x4', merges: [{ position: 5, colSpan: 2, rowSpan: 2 }] },
  {
    id: '2+8', label: '2 + 8', hint: 'Two large windows side by side, eight below', gridSize: '4x4',
    merges: [{ position: 0, colSpan: 2, rowSpan: 2 }, { position: 2, colSpan: 2, rowSpan: 2 }],
  },
];

/**
 * Cameras poured into a new shape's windows: the LARGEST windows first (so the first camera
 * lands in the big one even when it sits mid-wall, as in "1 + 12"), then reading order. Any
 * beyond the shape's window count drop — visibly, before Save.
 */
export function flowCameras<T extends Slot>(grid: (T | null)[], gridSize: string, merges: WallMerge[]): (T | null)[] {
  const { rows, cols } = gridDims(gridSize);
  const cams = grid.filter(Boolean) as T[];
  const next: (T | null)[] = Array(rows * cols).fill(null);
  const order = [...buildWindows(gridSize, merges)]
    .sort((a, b) => b.rowSpan * b.colSpan - a.rowSpan * a.colSpan || a.position - b.position);
  order.forEach((w, i) => {
    if (cams[i]) next[w.position] = { ...cams[i], gridPosition: w.position };
  });
  return next;
}
